import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/apiConfig';
import { ArrowPathIcon, ExclamationTriangleIcon, SparklesIcon, PlusIcon, CheckCircleIcon, XCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

interface Video {
  id: number;
  title: string;
  url: string;
  description: string;
  transcript?: string;
  summary?: string;
  summary_source?: string;
  quiz?: {
    title?: string;
    multipleChoice?: Array<{
      id: number;
      question: string;
      options: Array<{ label: string; text: string }>;
      correctAnswer: string;
      explanation?: string;
    }>;
    shortAnswer?: Array<{
      id: number;
      question: string;
      sampleAnswer?: string;
    }>;
  };
  watched: boolean;
  video_file?: string;
  playback_url?: string;
  created_at: string;
  updated_at: string;
}

const VideoList: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedTranscripts, setExpandedTranscripts] = useState<Set<number>>(new Set());
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [fetchingVideo, setFetchingVideo] = useState(false);
  const [fetchingTranscript, setFetchingTranscript] = useState<{ [key: number]: boolean }>({});
  const [recentlyImported, setRecentlyImported] = useState<Set<number>>(new Set());
  const [summarizing, setSummarizing] = useState<{ [key: number]: boolean }>({});
  const [generatingQuiz, setGeneratingQuiz] = useState<{ [key: number]: boolean }>({});
  const [quizAnswers, setQuizAnswers] = useState<{ [videoId: number]: { [questionId: string]: string } }>({});
  const [quizSubmitted, setQuizSubmitted] = useState<{ [videoId: number]: boolean }>({});
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  // Upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [playingVideoId, setPlayingVideoId] = useState<number | null>(null);

  useEffect(() => {
    fetchVideos();
  }, []); // Only fetch on mount

  useEffect(() => {
    // Only auto-poll if there are videos actively being transcribed or recently imported
    const hasActiveTranscription = videos.some(v =>
      !v.transcript && (fetchingTranscript[v.id] || recentlyImported.has(v.id))
    );

    if (!hasActiveTranscription) {
      return; // No active transcriptions, skip polling
    }

    // Poll every 30 seconds when there are active transcriptions
    const pollInterval = setInterval(() => {
      fetchVideos();
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [videos, fetchingTranscript, recentlyImported]); // Re-evaluate when these change

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/videos/');
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setError('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchFromUrl = async () => {
    if (!newVideoUrl.trim()) {
      alert('Please enter a video URL');
      return;
    }

    setFetchingVideo(true);
    try {
      const response = await apiClient.post('/api/videos/fetch_from_url/', {
        url: newVideoUrl
      });

      const newVideo = response.data.video;
      setVideos([newVideo, ...videos]);
      setNewVideoUrl('');

      // Track as recently imported (for showing auto-transcription status)
      setRecentlyImported(prev => new Set(prev).add(newVideo.id));

      // Remove from recently imported after 5 minutes
      setTimeout(() => {
        setRecentlyImported(prev => {
          const newSet = new Set(prev);
          newSet.delete(newVideo.id);
          return newSet;
        });
      }, 300000); // 5 minutes

      alert('Video imported successfully! Transcript will be fetched automatically.');
    } catch (error: any) {
      console.error('Error fetching video:', error);
      alert(error.response?.data?.error || 'Failed to fetch video from URL');
    } finally {
      setFetchingVideo(false);
    }
  };

  const handleUploadVideo = async () => {
    if (!uploadFile) {
      alert('Please select a video file');
      return;
    }

    const title = uploadTitle.trim() || uploadFile.name.replace(/\.[^/.]+$/, '');
    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned upload URL
      const urlResponse = await apiClient.post('/api/videos/generate_upload_url/', {
        filename: uploadFile.name,
        content_type: uploadFile.type || 'video/mp4',
        title,
      });

      const { video_id, upload_url } = urlResponse.data;
      setUploadProgress(10);

      // Step 2: Upload file directly to S3
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url, true);
        xhr.setRequestHeader('Content-Type', uploadFile.type || 'video/mp4');

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = 10 + Math.round((event.loaded / event.total) * 80);
            setUploadProgress(percent);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(uploadFile);
      });

      setUploadProgress(95);

      // Step 3: Notify backend upload is complete
      const completeResponse = await apiClient.post(`/api/videos/${video_id}/complete_upload/`, { title });
      const newVideo = completeResponse.data.video;
      setVideos([newVideo, ...videos]);

      setUploadProgress(100);
      setUploadFile(null);
      setUploadTitle('');
      alert('Video uploaded successfully!');
    } catch (error: any) {
      console.error('Error uploading video:', error);
      alert(error.response?.data?.error || error.message || 'Failed to upload video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (videoId: number, videoTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${videoTitle}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/videos/${videoId}/`);
      setVideos(videos.filter(video => video.id !== videoId));
    } catch (error: any) {
      console.error('Error deleting video:', error);
      alert(error.response?.data?.error || 'Failed to delete video');
    }
  };

  const handleGenerateSummary = async (videoId: number) => {
    setSummarizing(prev => ({ ...prev, [videoId]: true }));
    try {
      const response = await apiClient.post(`/api/videos/${videoId}/generate_summary/`, {
        model: 'gpt-4o-mini'
      });

      setVideos(prevVideos => prevVideos.map(video => {
        if (video.id === videoId) {
          return { ...video, summary: response.data.summary };
        }
        return video;
      }));
    } catch (error: any) {
      console.error('Error generating summary:', error);
      alert(error.response?.data?.error || 'Failed to generate summary');
    } finally {
      setSummarizing(prev => ({ ...prev, [videoId]: false }));
    }
  };

  const handleFetchTranscript = async (videoId: number) => {
    setFetchingTranscript(prev => ({ ...prev, [videoId]: true }));
    try {
      const response = await apiClient.post(`/api/videos/${videoId}/fetch_transcript/`);

      if (response.data.success) {
        alert('Transcript fetching started in background. Please refresh the page in a few minutes to see the transcript.');

        // Poll for transcript updates every 10 seconds
        const pollInterval = setInterval(async () => {
          try {
            const videoResponse = await apiClient.get(`/api/videos/${videoId}/`);
            const updatedVideo = videoResponse.data;

            if (updatedVideo.transcript && updatedVideo.transcript.trim()) {
              // Transcript is now available
              setVideos(prevVideos => prevVideos.map(v =>
                v.id === videoId ? updatedVideo : v
              ));
              setFetchingTranscript(prev => ({ ...prev, [videoId]: false }));
              clearInterval(pollInterval);
            }
          } catch (err) {
            console.error('Error polling for transcript:', err);
          }
        }, 10000);

        // Stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setFetchingTranscript(prev => ({ ...prev, [videoId]: false }));
        }, 300000);
      } else {
        alert(response.data.message || 'Failed to fetch transcript');
        setFetchingTranscript(prev => ({ ...prev, [videoId]: false }));
      }
    } catch (error: any) {
      console.error('Error fetching transcript:', error);
      alert(error.response?.data?.error || 'Failed to fetch transcript');
      setFetchingTranscript(prev => ({ ...prev, [videoId]: false }));
    }
  };

  const handleGenerateQuiz = async (videoId: number) => {
    setGeneratingQuiz(prev => ({ ...prev, [videoId]: true }));
    try {
      const response = await apiClient.post(`/api/videos/${videoId}/generate_quiz/`, {
        model: 'gpt-4o',
        use_summary: true
      });

      setVideos(prevVideos => prevVideos.map(video => {
        if (video.id === videoId) {
          return { ...video, quiz: response.data.quiz };
        }
        return video;
      }));
    } catch (error: any) {
      console.error('Error generating quiz:', error);
      alert(error.response?.data?.error || 'Failed to generate quiz');
    } finally {
      setGeneratingQuiz(prev => ({ ...prev, [videoId]: false }));
    }
  };

  const handleQuizAnswerChange = (videoId: number, questionId: string, answer: string) => {
    setQuizAnswers(prev => ({
      ...prev,
      [videoId]: {
        ...(prev[videoId] || {}),
        [questionId]: answer
      }
    }));
  };

  const handleSubmitQuiz = (videoId: number, video: Video) => {
    setQuizSubmitted(prev => ({ ...prev, [videoId]: true }));
  };

  const handleResetQuiz = (videoId: number) => {
    setQuizAnswers(prev => ({ ...prev, [videoId]: {} }));
    setQuizSubmitted(prev => ({ ...prev, [videoId]: false }));
  };

  const calculateQuizScore = (videoId: number, video: Video) => {
    if (!video.quiz?.multipleChoice) return { correct: 0, total: 0 };

    const answers = quizAnswers[videoId] || {};
    let correct = 0;
    const total = video.quiz.multipleChoice.length;

    video.quiz.multipleChoice.forEach(question => {
      const questionId = `mc-${question.id}`;
      if (answers[questionId] === question.correctAnswer) {
        correct++;
      }
    });

    return { correct, total };
  };

  const toggleExpanded = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const toggleTranscript = (id: number) => {
    setExpandedTranscripts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '128px' }}>
        <ArrowPathIcon style={{ width: '32px', height: '32px', color: COLORS.accent.default, animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ borderRadius: RADIUS, backgroundColor: '#FEF2F2', padding: SPACING[16] }}>
        <div style={{ display: 'flex' }}>
          <div style={{ flexShrink: 0 }}>
            <ExclamationTriangleIcon style={{ width: '20px', height: '20px', color: '#F87171' }} aria-hidden="true" />
          </div>
          <div style={{ marginLeft: SPACING[12] }}>
            <h3 style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: '#991B1B' }}>{error}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {/* URL Import Section */}
      <div style={{ backgroundColor: COLORS.bg.panel, borderRadius: RADIUS, padding: SPACING[16], border: `1px solid ${COLORS.border.default}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[12] }}>
          <h3 style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>Import Video from URL</h3>
          <button
            onClick={fetchVideos}
            disabled={loading}
            onMouseEnter={() => setHoveredButton('refresh')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: `${SPACING[4]} ${SPACING[12]}`,
              fontSize: TYPOGRAPHY.body.sizes.tiny,
              fontWeight: TYPOGRAPHY.weight.medium,
              color: COLORS.text.secondary,
              backgroundColor: hoveredButton === 'refresh' ? COLORS.bg.hover : COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1
            }}
            title="Refresh videos"
          >
            <ArrowPathIcon style={{ width: '16px', height: '16px', marginRight: SPACING[4], animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
        <div style={{ display: 'flex', gap: SPACING[8] }}>
          <input
            type="text"
            value={newVideoUrl}
            onChange={(e) => setNewVideoUrl(e.target.value)}
            placeholder="Paste YouTube video URL here..."
            style={{
              flex: 1,
              padding: `${SPACING[8]} ${SPACING[12]}`,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.body.sizes.default,
              outline: 'none'
            }}
          />
          <button
            onClick={handleFetchFromUrl}
            disabled={fetchingVideo}
            onMouseEnter={() => setHoveredButton('import')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: `${SPACING[8]} ${SPACING[16]}`,
              border: 'none',
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: TYPOGRAPHY.weight.medium,
              color: '#FFFFFF',
              backgroundColor: fetchingVideo ? COLORS.accent.default : (hoveredButton === 'import' ? COLORS.accent.hover : COLORS.accent.default),
              cursor: fetchingVideo ? 'not-allowed' : 'pointer',
              opacity: fetchingVideo ? 0.5 : 1
            }}
          >
            {fetchingVideo ? (
              <>
                <ArrowPathIcon style={{ width: '16px', height: '16px', marginRight: SPACING[8], animation: 'spin 1s linear infinite' }} />
                Fetching...
              </>
            ) : (
              <>
                <PlusIcon style={{ width: '16px', height: '16px', marginRight: SPACING[8] }} />
                Import Video
              </>
            )}
          </button>
        </div>
      </div>

      {/* File Upload Section */}
      <div style={{ backgroundColor: COLORS.bg.panel, borderRadius: RADIUS, padding: SPACING[16], border: `1px solid ${COLORS.border.default}` }}>
        <h3 style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[12] }}>Upload Video File</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
          <div style={{ display: 'flex', gap: SPACING[8] }}>
            <input
              type="text"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              placeholder="Video title (optional, defaults to filename)"
              style={{
                flex: 1,
                padding: `${SPACING[8]} ${SPACING[12]}`,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                fontSize: TYPOGRAPHY.body.sizes.default,
                outline: 'none'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: SPACING[8], alignItems: 'center' }}>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              style={{
                flex: 1,
                padding: SPACING[8],
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                fontSize: TYPOGRAPHY.body.sizes.small,
              }}
            />
            <button
              onClick={handleUploadVideo}
              disabled={uploading || !uploadFile}
              onMouseEnter={() => setHoveredButton('upload')}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: `${SPACING[8]} ${SPACING[16]}`,
                border: 'none',
                borderRadius: RADIUS,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: TYPOGRAPHY.weight.medium,
                color: '#FFFFFF',
                backgroundColor: uploading || !uploadFile
                  ? COLORS.text.tertiary
                  : (hoveredButton === 'upload' ? COLORS.accent.hover : COLORS.accent.default),
                cursor: uploading || !uploadFile ? 'not-allowed' : 'pointer',
              }}
            >
              {uploading ? (
                <>
                  <ArrowPathIcon style={{ width: '16px', height: '16px', marginRight: SPACING[8], animation: 'spin 1s linear infinite' }} />
                  Uploading...
                </>
              ) : (
                <>
                  <PlusIcon style={{ width: '16px', height: '16px', marginRight: SPACING[8] }} />
                  Upload
                </>
              )}
            </button>
          </div>
          {uploading && (
            <div style={{ width: '100%' }}>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: COLORS.border.default,
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${uploadProgress}%`,
                  height: '100%',
                  backgroundColor: COLORS.accent.default,
                  borderRadius: '4px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <p style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.secondary, marginTop: SPACING[4] }}>
                {uploadProgress < 10 ? 'Preparing upload...' :
                 uploadProgress < 90 ? `Uploading to S3... ${uploadProgress}%` :
                 uploadProgress < 100 ? 'Finalizing...' : 'Complete!'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Video List */}
      <div style={{ backgroundColor: COLORS.bg.panel, borderRadius: RADIUS, border: `1px solid ${COLORS.border.default}` }}>
        {videos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: `${SPACING[48]} 0` }}>
            <p style={{ color: COLORS.text.secondary }}>No videos yet. Import one from a URL above!</p>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {videos.map((video, index) => (
              <li
                key={video.id}
                style={{
                  padding: SPACING[16],
                  backgroundColor: hoveredItem === video.id ? COLORS.bg.hover : 'transparent',
                  borderBottom: index < videos.length - 1 ? `1px solid ${COLORS.border.default}` : 'none'
                }}
                onMouseEnter={() => setHoveredItem(video.id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleExpanded(video.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ fontSize: TYPOGRAPHY.size.lg, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>{video.title}</h3>
                      <p style={{ marginTop: SPACING[4], fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>{video.description}</p>
                      <div style={{ marginTop: SPACING[8], display: 'flex', alignItems: 'center', gap: SPACING[8], fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>
                        <span style={{
                          padding: `${SPACING[4]} ${SPACING[8]}`,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          borderRadius: '9999px',
                          backgroundColor: video.watched ? '#D1FAE5' : '#FEF3C7',
                          color: video.watched ? '#065F46' : '#92400E'
                        }}>
                          {video.watched ? 'Watched' : 'Not Watched'}
                        </span>
                        {video.video_file && (
                          <span style={{
                            padding: `${SPACING[4]} ${SPACING[8]}`,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            borderRadius: '9999px',
                            backgroundColor: '#E0F2FE',
                            color: '#0369A1'
                          }}>
                            Uploaded
                          </span>
                        )}
                        {video.transcript && (
                          <span style={{
                            padding: `${SPACING[4]} ${SPACING[8]}`,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            borderRadius: '9999px',
                            backgroundColor: '#DBEAFE',
                            color: '#1E40AF'
                          }}>
                            Has Transcript
                          </span>
                        )}
                        {video.summary && (
                          <span style={{
                            padding: `${SPACING[4]} ${SPACING[8]}`,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            borderRadius: '9999px',
                            backgroundColor: '#EDE9FE',
                            color: '#5B21B6'
                          }}>
                            Has Summary
                          </span>
                        )}
                        {video.quiz && (video.quiz.multipleChoice?.length || video.quiz.shortAnswer?.length) && (
                          <span style={{
                            padding: `${SPACING[4]} ${SPACING[8]}`,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            borderRadius: '9999px',
                            backgroundColor: '#FFEDD5',
                            color: '#C2410C'
                          }}>
                            Has Quiz
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ marginLeft: SPACING[16], display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                      {video.video_file ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPlayingVideoId(playingVideoId === video.id ? null : video.id);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: `${SPACING[8]} ${SPACING[16]}`,
                            border: 'none',
                            borderRadius: RADIUS,
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: '#FFFFFF',
                            backgroundColor: COLORS.accent.default,
                            cursor: 'pointer'
                          }}
                        >
                          {playingVideoId === video.id ? 'Hide Player' : 'Play Video'}
                        </button>
                      ) : video.url ? (
                        <a
                          href={video.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: `${SPACING[8]} ${SPACING[16]}`,
                            border: 'none',
                            borderRadius: RADIUS,
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: '#FFFFFF',
                            backgroundColor: COLORS.accent.default,
                            textDecoration: 'none'
                          }}
                        >
                          Watch Video
                        </a>
                      ) : null}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(video.id, video.title);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: `${SPACING[8]} ${SPACING[12]}`,
                          border: '1px solid #FCA5A5',
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          color: '#B91C1C',
                          backgroundColor: COLORS.bg.panel,
                          cursor: 'pointer'
                        }}
                        title="Delete video"
                      >
                        <svg style={{ width: '16px', height: '16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                      <button style={{ color: COLORS.text.tertiary, background: 'none', border: 'none', cursor: 'pointer' }}>
                        {expandedId === video.id ? (
                          <svg style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        ) : (
                          <svg style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Inline Video Player for uploaded videos */}
                {playingVideoId === video.id && video.playback_url && (
                  <div style={{ marginTop: SPACING[12] }} onClick={(e) => e.stopPropagation()}>
                    <video
                      controls
                      style={{ width: '100%', maxHeight: '480px', borderRadius: RADIUS, backgroundColor: '#000' }}
                      src={video.playback_url}
                    >
                      Your browser does not support video playback.
                    </video>
                  </div>
                )}

                {/* Always Visible: Summary, Quiz, Transcript Header */}
                {expandedId === video.id && (
                  <div style={{ marginTop: SPACING[16], display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                    {/* AI Summary Section - Always Visible */}
                    {(video.transcript || video.description) && (
                      <div style={{ backgroundColor: '#FAF5FF', border: '1px solid #E9D5FF', borderRadius: RADIUS, padding: SPACING[12] }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[8] }}>
                          <h4 style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: '#581C87', display: 'flex', alignItems: 'center' }}>
                            <SparklesIcon style={{ width: '16px', height: '16px', marginRight: SPACING[4] }} />
                            AI Summary
                          </h4>
                          {!video.summary && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGenerateSummary(video.id);
                              }}
                              disabled={summarizing[video.id]}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: `${SPACING[4]} ${SPACING[8]}`,
                                fontSize: TYPOGRAPHY.body.sizes.tiny,
                                fontWeight: TYPOGRAPHY.weight.medium,
                                borderRadius: RADIUS,
                                color: '#6B21A8',
                                backgroundColor: '#F3E8FF',
                                border: 'none',
                                cursor: summarizing[video.id] ? 'not-allowed' : 'pointer',
                                opacity: summarizing[video.id] ? 0.5 : 1
                              }}
                            >
                              <SparklesIcon style={{ width: '12px', height: '12px', marginRight: SPACING[4] }} />
                              {summarizing[video.id] ? 'Generating...' : 'Generate Summary'}
                            </button>
                          )}
                        </div>
                        {video.summary ? (
                          <>
                            {video.summary_source === 'description' && (
                              <div style={{ marginBottom: SPACING[8], padding: SPACING[8], backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: RADIUS, fontSize: TYPOGRAPHY.body.sizes.tiny, color: '#92400E' }}>
                                Warning: This summary was generated from the video description, not the full transcript.
                                {!video.transcript && ' Transcript is still being processed.'}
                              </div>
                            )}
                            <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, whiteSpace: 'pre-wrap' }}>
                              {video.summary}
                            </div>
                          </>
                        ) : (
                          <p style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.secondary, fontStyle: 'italic' }}>
                            No summary yet. Click "Generate Summary" to create an AI-powered summary.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Quiz Section - Always Visible */}
                    <div style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: RADIUS, padding: SPACING[16] }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[12] }}>
                        <h4 style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: '#9A3412', display: 'flex', alignItems: 'center' }}>
                          <QuestionMarkCircleIcon style={{ width: '16px', height: '16px', marginRight: SPACING[4] }} />
                          Quiz
                        </h4>
                        {!(video.quiz && (video.quiz.multipleChoice?.length || video.quiz.shortAnswer?.length)) && (video.transcript || video.summary) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateQuiz(video.id);
                            }}
                            disabled={generatingQuiz[video.id]}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: `${SPACING[4]} ${SPACING[8]}`,
                              fontSize: TYPOGRAPHY.body.sizes.tiny,
                              fontWeight: TYPOGRAPHY.weight.medium,
                              borderRadius: RADIUS,
                              color: '#C2410C',
                              backgroundColor: '#FFEDD5',
                              border: 'none',
                              cursor: generatingQuiz[video.id] ? 'not-allowed' : 'pointer',
                              opacity: generatingQuiz[video.id] ? 0.5 : 1
                            }}
                          >
                            <SparklesIcon style={{ width: '12px', height: '12px', marginRight: SPACING[4] }} />
                            {generatingQuiz[video.id] ? 'Generating...' : 'Generate Quiz'}
                          </button>
                        )}
                      </div>

                      {video.quiz && (video.quiz.multipleChoice?.length || video.quiz.shortAnswer?.length) ? (
                        <div style={{ backgroundColor: COLORS.bg.panel, borderRadius: RADIUS, padding: SPACING[12] }}>
                          {/* Quiz Score Display */}
                          {quizSubmitted[video.id] && video.quiz.multipleChoice && video.quiz.multipleChoice.length > 0 && (
                            <div style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: RADIUS, padding: SPACING[16], marginBottom: SPACING[16] }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                  <p style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: '#1E3A8A' }}>Quiz Results</p>
                                  <p style={{ color: '#1D4ED8', marginTop: SPACING[4] }}>
                                    You scored <span style={{ fontWeight: TYPOGRAPHY.weight.bold, fontSize: TYPOGRAPHY.size.xl }}>{calculateQuizScore(video.id, video).correct}</span> out of <span style={{ fontWeight: TYPOGRAPHY.weight.bold, fontSize: TYPOGRAPHY.size.xl }}>{calculateQuizScore(video.id, video).total}</span>
                                    <span style={{ marginLeft: SPACING[8] }}>({Math.round((calculateQuizScore(video.id, video).correct / calculateQuizScore(video.id, video).total) * 100)}%)</span>
                                  </p>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResetQuiz(video.id);
                                  }}
                                  style={{
                                    padding: `${SPACING[4]} ${SPACING[12]}`,
                                    fontSize: TYPOGRAPHY.body.sizes.small,
                                    fontWeight: TYPOGRAPHY.weight.medium,
                                    color: '#1D4ED8',
                                    backgroundColor: '#DBEAFE',
                                    borderRadius: RADIUS,
                                    border: 'none',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Retake Quiz
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Multiple Choice Questions */}
                          {video.quiz.multipleChoice && video.quiz.multipleChoice.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16], marginBottom: SPACING[16] }}>
                              <h5 style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary, borderBottom: `1px solid ${COLORS.border.default}`, paddingBottom: SPACING[8] }}>Multiple Choice Questions</h5>
                              {video.quiz.multipleChoice.map((question, index) => {
                                const questionId = `mc-${question.id}`;
                                const userAnswer = quizAnswers[video.id]?.[questionId];
                                const isCorrect = userAnswer === question.correctAnswer;

                                return (
                                  <div key={question.id} style={{ border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS, padding: SPACING[12], backgroundColor: COLORS.bg.base }}>
                                    <p style={{ fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[8], fontSize: TYPOGRAPHY.body.sizes.small }}>
                                      {index + 1}. {question.question}
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
                                      {question.options.map((option) => (
                                        <label
                                          key={option.label}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: SPACING[8],
                                            border: `1px solid ${
                                              quizSubmitted[video.id]
                                                ? option.label === question.correctAnswer
                                                  ? '#86EFAC'
                                                  : userAnswer === option.label && !isCorrect
                                                  ? '#FCA5A5'
                                                  : COLORS.border.default
                                                : userAnswer === option.label
                                                ? '#93C5FD'
                                                : COLORS.border.default
                                            }`,
                                            borderRadius: RADIUS,
                                            cursor: quizSubmitted[video.id] ? 'default' : 'pointer',
                                            fontSize: TYPOGRAPHY.body.sizes.small,
                                            backgroundColor: quizSubmitted[video.id]
                                              ? option.label === question.correctAnswer
                                                ? '#F0FDF4'
                                                : userAnswer === option.label && !isCorrect
                                                ? '#FEF2F2'
                                                : COLORS.bg.panel
                                              : userAnswer === option.label
                                              ? '#EFF6FF'
                                              : COLORS.bg.panel
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <input
                                            type="radio"
                                            name={`${video.id}-${questionId}`}
                                            value={option.label}
                                            checked={userAnswer === option.label}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              handleQuizAnswerChange(video.id, questionId, e.target.value);
                                            }}
                                            disabled={quizSubmitted[video.id]}
                                            style={{ width: '16px', height: '16px', accentColor: COLORS.accent.default }}
                                          />
                                          <span style={{ marginLeft: SPACING[8], color: COLORS.text.secondary }}>
                                            <span style={{ fontWeight: TYPOGRAPHY.weight.medium }}>{option.label})</span> {option.text}
                                          </span>
                                          {quizSubmitted[video.id] && option.label === question.correctAnswer && (
                                            <CheckCircleIcon style={{ width: '16px', height: '16px', color: '#16A34A', marginLeft: 'auto' }} />
                                          )}
                                          {quizSubmitted[video.id] && userAnswer === option.label && !isCorrect && (
                                            <XCircleIcon style={{ width: '16px', height: '16px', color: '#DC2626', marginLeft: 'auto' }} />
                                          )}
                                        </label>
                                      ))}
                                    </div>
                                    {quizSubmitted[video.id] && question.explanation && (
                                      <div style={{ marginTop: SPACING[8], padding: SPACING[8], backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: RADIUS }}>
                                        <p style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: '#1E3A8A' }}>
                                          <span style={{ fontWeight: TYPOGRAPHY.weight.semibold }}>Explanation:</span> {question.explanation}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Short Answer Questions */}
                          {video.quiz.shortAnswer && video.quiz.shortAnswer.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16], marginBottom: SPACING[16] }}>
                              <h5 style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary, borderBottom: `1px solid ${COLORS.border.default}`, paddingBottom: SPACING[8] }}>Short Answer Questions</h5>
                              {video.quiz.shortAnswer.map((question, index) => {
                                const questionId = `sa-${question.id}`;
                                const userAnswer = quizAnswers[video.id]?.[questionId];

                                return (
                                  <div key={question.id} style={{ border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS, padding: SPACING[12], backgroundColor: COLORS.bg.base }}>
                                    <p style={{ fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[8], fontSize: TYPOGRAPHY.body.sizes.small }}>
                                      {(video.quiz?.multipleChoice?.length || 0) + index + 1}. {question.question}
                                    </p>
                                    <textarea
                                      value={userAnswer || ''}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleQuizAnswerChange(video.id, questionId, e.target.value);
                                      }}
                                      disabled={quizSubmitted[video.id]}
                                      rows={3}
                                      style={{
                                        width: '100%',
                                        padding: SPACING[8],
                                        fontSize: TYPOGRAPHY.body.sizes.small,
                                        border: `1px solid ${COLORS.border.default}`,
                                        borderRadius: RADIUS,
                                        resize: 'vertical',
                                        backgroundColor: quizSubmitted[video.id] ? COLORS.bg.base : COLORS.bg.panel
                                      }}
                                      placeholder="Type your answer here..."
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    {quizSubmitted[video.id] && question.sampleAnswer && (
                                      <div style={{ marginTop: SPACING[8], padding: SPACING[8], backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: RADIUS }}>
                                        <p style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: '#166534' }}>
                                          <span style={{ fontWeight: TYPOGRAPHY.weight.semibold }}>Sample Answer:</span> {question.sampleAnswer}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Submit Button */}
                          {!quizSubmitted[video.id] && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSubmitQuiz(video.id, video);
                                }}
                                style={{
                                  padding: `${SPACING[8]} ${SPACING[16]}`,
                                  fontSize: TYPOGRAPHY.body.sizes.small,
                                  fontWeight: TYPOGRAPHY.weight.medium,
                                  color: '#FFFFFF',
                                  backgroundColor: '#EA580C',
                                  borderRadius: RADIUS,
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                              >
                                Submit Quiz
                              </button>
                            </div>
                          )}
                        </div>
                      ) : !video.transcript ? (
                        <p style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.secondary, fontStyle: 'italic' }}>
                          Quiz generation requires a transcript. {recentlyImported.has(video.id) ? 'Transcript is being fetched automatically...' : 'Please fetch the transcript first.'}
                        </p>
                      ) : (
                        <p style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.secondary, fontStyle: 'italic' }}>
                          No quiz yet. Click "Generate Quiz" to create AI-powered quiz questions.
                        </p>
                      )}
                    </div>

                    {/* Transcript Section - Collapsible */}
                    <div style={{ backgroundColor: COLORS.bg.base, border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS, padding: SPACING[12] }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[8] }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTranscript(video.id);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <h4 style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary }}>Transcript</h4>
                          {video.transcript && (
                            <svg
                              style={{
                                width: '16px',
                                height: '16px',
                                marginLeft: SPACING[8],
                                transition: 'transform 0.2s',
                                transform: expandedTranscripts.has(video.id) ? 'rotate(180deg)' : 'rotate(0deg)'
                              }}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </button>
                        {!video.transcript && !fetchingTranscript[video.id] && !recentlyImported.has(video.id) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFetchTranscript(video.id);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: `${SPACING[4]} ${SPACING[8]}`,
                              fontSize: TYPOGRAPHY.body.sizes.tiny,
                              fontWeight: TYPOGRAPHY.weight.medium,
                              borderRadius: RADIUS,
                              color: '#1E40AF',
                              backgroundColor: '#DBEAFE',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                            title="Manually retry transcript fetching"
                          >
                            <svg style={{ width: '12px', height: '12px', marginRight: SPACING[4] }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Retry Fetch
                          </button>
                        )}
                      </div>
                      {video.transcript && expandedTranscripts.has(video.id) ? (
                        <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, maxHeight: '256px', overflowY: 'auto', whiteSpace: 'pre-wrap', marginTop: SPACING[8] }}>
                          {video.transcript}
                        </div>
                      ) : video.transcript ? (
                        <p style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.secondary, fontStyle: 'italic', marginTop: SPACING[8] }}>
                          Click "Transcript" to expand and view the full transcript.
                        </p>
                      ) : fetchingTranscript[video.id] ? (
                        <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: '#2563EB', fontStyle: 'italic', display: 'flex', alignItems: 'center' }}>
                          <svg style={{ width: '12px', height: '12px', marginRight: SPACING[8], animation: 'spin 1s linear infinite' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Fetching transcript manually... This may take 2-5 minutes.
                        </div>
                      ) : recentlyImported.has(video.id) ? (
                        <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: '#16A34A', fontStyle: 'italic', display: 'flex', alignItems: 'center' }}>
                          <svg style={{ width: '12px', height: '12px', marginRight: SPACING[8], animation: 'spin 1s linear infinite' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Transcript is being fetched automatically in background... This may take 2-5 minutes. Page will auto-refresh.
                        </div>
                      ) : (
                        <p style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.secondary, fontStyle: 'italic' }}>
                          Transcript not available. This video may have been imported before auto-transcription was enabled. Click "Retry Fetch" to get transcript.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default VideoList;
